import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { luxonFormats, luxonViews } from "../../../EConsent/Builder/blockTypes";
import { resolveSignaturePlaceholder } from "../../../EConsent/Builder/consentPlaceholders";
import { useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import {
  setProviderResponse,
  updateBlockEditable,
} from "../../../../Redux/reducers/providerConsentSlice";
import { errorToastMessage, toastMessage } from "../../../../utils/toast";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

const ProviderSignature = ({
  isName,
  isAddress,
  isDate,
  response,
  changeHandler,
  clear,
  sigCanvas,
  format,
  submitSignature,
  placeholders,
}: any) => {
  const { t } = useTranslation("econsent");
  return (
    <>
      {isName && (
        <Stack gap={1}>
          <Stack gap={1}>
            <Typography variant="subtitle1" fontWeight="medium">
              {t("BUILDER_UI.PREVIEW_FIRST_NAME")}
            </Typography>
            <TextField
              name="firstName"
              sx={{
                width: {
                  md: 350,
                  xs: "100%",
                },
                maxWidth: "100%",
              }}
              placeholder={placeholders.firstName}
              value={response?.firstName || ""}
              onChange={(e) => changeHandler(e.target.value, "firstName")}
            />
          </Stack>
          <Stack gap={1}>
            <Typography variant="subtitle1" fontWeight="medium">
              {t("BUILDER_UI.PREVIEW_LAST_NAME")}
            </Typography>
            <TextField
              name="lastName"
              sx={{
                width: {
                  md: 350,
                  xs: "100%",
                },
                maxWidth: "100%",
              }}
              placeholder={placeholders.lastName}
              value={response?.lastName || ""}
              onChange={(e) => changeHandler(e.target.value, "lastName")}
            />
          </Stack>
        </Stack>
      )}
      {isAddress && (
        <Stack gap={1}>
          <Typography variant="subtitle1" fontWeight="medium">
            {t("BUILDER_UI.PREVIEW_ADDRESS")}
          </Typography>
          <TextField
            multiline
            rows={4}
            name="address"
            sx={{
              width: {
                md: 350,
                xs: "100%",
              },
              maxWidth: "100%",
            }}
            placeholder={placeholders.address}
            value={response?.address || ""}
            onChange={(e) => changeHandler(e.target.value, "address")}
          />
        </Stack>
      )}
      {isDate && (
        <Stack gap={1}>
          <Typography variant="subtitle1" fontWeight="medium">
            {t("BUILDER_UI.PREVIEW_DATE")}
          </Typography>

          <DateTimePicker
            format={luxonFormats?.[format]}
            views={luxonViews?.[format]}
            value={
              response?.date
                ? DateTime.fromFormat(response?.date, luxonFormats?.[format])
                : null
            }
            onChange={(newDate) =>
              changeHandler(newDate?.toFormat(luxonFormats?.[format]), "date")
            }
            slotProps={{
              textField: {
                sx: {
                  width: {
                    md: 350,
                    xs: "100%",
                  },
                  maxWidth: "100%",
                },
                InputProps: {
                  readOnly: true,
                  placeholder: placeholders.date,
                },
              },
              field: {
                readOnly: true,
              },
            }}
          />
        </Stack>
      )}
      <Stack gap={1}>
        <Typography variant="subtitle1" fontWeight="medium">
          {t("BUILDER_UI.PREVIEW_SIGNATURE_LABEL")}
        </Typography>
        {response?.signature ? (
          <img
            src={response?.signature}
            alt={t("SIGNATURE_FIELD.SIGNATURE_ALT")}
            style={{
              borderRadius: "6px",
              width: "300px",
            }}
          />
        ) : (
          <Box
            sx={{
              width: {
                md: "80%",
                xs: "100%",
              },
            }}
          >
            <SignatureCanvas
              ref={(ref) => {
                sigCanvas.current = ref;
              }}
              canvasProps={{ className: "canvas-element" }}
            />
            <Box my={2}>
              <Button variant="contained" onClick={submitSignature}>
                {t("SIGNATURE_FIELD.SAVE_SIGNATURE")}
              </Button>
              <Button sx={{ ml: "10px" }} variant="outlined" onClick={clear}>
                {t("SIGNATURE_FIELD.CLEAR")}
              </Button>
            </Box>
          </Box>
        )}
      </Stack>
    </>
  );
};

const PatientSignature = ({
  isName,
  isAddress,
  isDate,
  response,
}: {
  isName: boolean;
  isAddress: boolean;
  isDate: boolean;
  response: any;
}) => {
  const { t } = useTranslation("econsent");
  return (
    <>
      {isName && (
        <Stack gap={1}>
          <Stack gap={1}>
            <Typography variant="subtitle1" fontWeight="medium">
              {t("BUILDER_UI.PREVIEW_FIRST_NAME")}
            </Typography>
            <Typography variant="body1">
              {response?.firstName || "-"}
            </Typography>
          </Stack>
          <Stack gap={1}>
            <Typography variant="subtitle1" fontWeight="medium">
              {t("BUILDER_UI.PREVIEW_LAST_NAME")}
            </Typography>
            <Typography variant="body1">{response?.lastName || "-"}</Typography>
          </Stack>
        </Stack>
      )}
      {isAddress && (
        <Stack gap={1}>
          <Typography variant="subtitle1" fontWeight="medium">
            {t("BUILDER_UI.PREVIEW_ADDRESS")}
          </Typography>
          <Typography variant="body1">{response?.address || "-"}</Typography>
        </Stack>
      )}
      {isDate && (
        <Stack gap={1}>
          <Typography variant="subtitle1" fontWeight="medium">
            {t("BUILDER_UI.PREVIEW_DATE")}
          </Typography>
          <Typography variant="body1">{response?.date || "-"}</Typography>
        </Stack>
      )}
      <Stack gap={1}>
        <Typography variant="subtitle1" fontWeight="medium">
          {t("BUILDER_UI.PREVIEW_SIGNATURE_LABEL")}
        </Typography>
        {response?.signature ? (
          <img
            src={response?.signature}
            alt={t("SIGNATURE_FIELD.SIGNATURE_ALT")}
            style={{
              borderRadius: "6px",
              width: "300px",
            }}
          />
        ) : (
          <Typography variant="body1">{response?.signature || "-"}</Typography>
        )}
      </Stack>
    </>
  );
};

type Props = {
  currentBlock: any;
};

const SignatureField = ({ currentBlock }: Props) => {
  const { t } = useTranslation("econsent");
  const dispatch = useAppDispatch();

  const readonly = useAppSelector((state) => state.providerConsent.readonly);
  const label = currentBlock?.label;
  const isName = currentBlock?.config?.name;
  const isAddress = currentBlock?.config?.address;
  const isDate = currentBlock?.config?.date;
  const format = currentBlock?.config?.format;
  const response = currentBlock?.response || {};
  const sigCanvas = useRef<any>();
  const placeholders = {
    firstName: resolveSignaturePlaceholder(
      currentBlock,
      "firstName",
      t("BUILDER_UI.PREVIEW_FIRST_NAME")
    ),
    lastName: resolveSignaturePlaceholder(
      currentBlock,
      "lastName",
      t("BUILDER_UI.PREVIEW_LAST_NAME")
    ),
    address: resolveSignaturePlaceholder(
      currentBlock,
      "address",
      t("BUILDER_UI.PREVIEW_ADDRESS")
    ),
    date: resolveSignaturePlaceholder(
      currentBlock,
      "date",
      t("BUILDER_UI.PREVIEW_SELECT_DATE")
    ),
    signature: resolveSignaturePlaceholder(
      currentBlock,
      "signature",
      t("BUILDER_UI.PREVIEW_SIGN_HERE")
    ),
  };

  const clear = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const submitSignature = async () => {
    try {
      if (!currentBlock?.response?.firstName && isName) {
        toastMessage("warning", t("SIGNATURE_FIELD.TOAST_FIRST_NAME_REQUIRED"));
        return;
      } else if (!currentBlock?.response?.lastName && isName) {
        toastMessage("warning", t("SIGNATURE_FIELD.TOAST_LAST_NAME_REQUIRED"));
        return;
      } else if (!currentBlock?.response?.address && isAddress) {
        toastMessage("warning", t("SIGNATURE_FIELD.TOAST_ADDRESS_REQUIRED"));
        return;
      } else if (!currentBlock?.response?.date && isDate) {
        toastMessage("warning", t("SIGNATURE_FIELD.TOAST_DATE_REQUIRED"));
        return;
      }

      if (sigCanvas.current) {
        if (sigCanvas.current.isEmpty()) {
          toastMessage("warning", t("SIGNATURE_FIELD.TOAST_SIGNATURE_REQUIRED"));
          return;
        }
      } else {
        toastMessage("warning", t("SIGNATURE_FIELD.TOAST_SIGNATURE_UNAVAILABLE"));
        return;
      }

      const image = sigCanvas.current.toDataURL();

      const updatedResponse: any = {
        signature: image,
      };

      if (isName) {
        updatedResponse.firstName = response?.firstName;
        updatedResponse.lastName = response?.lastName;
      }
      if (isAddress) {
        updatedResponse.address = response?.address;
      }
      if (isDate) {
        updatedResponse.date = response?.date;
      }

      dispatch(
        setProviderResponse({
          blockId: currentBlock?.id,
          response: {
            ...updatedResponse,
          },
        })
      );
      dispatch(
        updateBlockEditable({
          blockId: currentBlock?.id,
          editable: false,
        })
      );
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
    }
  };

  const changeHandler = (value: any, field: string) => {
    const updatedResponse = {
      ...response,
      [field]: value,
    };

    dispatch(
      setProviderResponse({
        blockId: currentBlock?.id,
        response: updatedResponse,
      })
    );
  };

  if (currentBlock?.config?.provider && !readonly) {
    return null;
  }

  const isReadonlyBlock = !!currentBlock?.config?.readonly;

  if (isReadonlyBlock) {
    return (
      <Stack
        gap={1}
        sx={{
          opacity: 0.65,
          pointerEvents: "none",
          bgcolor: "action.hover",
          borderRadius: 1,
          p: 2,
        }}
      >
        {label && (
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 500,
              color: "text.primary",
            }}
          >
            {label}
          </Typography>
        )}
        <PatientSignature
          isName={isName}
          isAddress={isAddress}
          isDate={isDate}
          response={response}
        />
      </Stack>
    );
  }

  return (
    <Stack gap={1}>
      {label && (
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 500,
            color: "text.primary",
          }}
        >
          {label}
        </Typography>
      )}
      {currentBlock.editable ? (
        <ProviderSignature
          isName={isName}
          isAddress={isAddress}
          isDate={isDate}
          format={format}
          response={response}
          sigCanvas={sigCanvas}
          changeHandler={changeHandler}
          clear={clear}
          submitSignature={submitSignature}
          placeholders={placeholders}
        />
      ) : (
        <PatientSignature
          isName={isName}
          isAddress={isAddress}
          isDate={isDate}
          response={response}
        />
      )}
    </Stack>
  );
};
export default SignatureField;
